import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { EventImage, EventResponse, Movie } from "@/types";

export function useEventImages(eventId: string, basePath = "/admin/events") {
  return useQuery({
    queryKey: ["event-images", basePath, eventId],
    queryFn: async () => {
      const event = await api.get<EventResponse>(`${basePath}/${eventId}`);
      return event.images;
    },
  });
}

export function useMovieImages(movieId: string) {
  return useQuery({
    queryKey: ["movie-images", movieId],
    queryFn: async () => {
      const movie = await api.get<Movie & { images: EventImage[] }>(`/admin/movies/${movieId}`);
      return movie.images;
    },
  });
}

interface UploadImageParams {
  file: File;
  entityType: "event" | "movie";
  entityId: string;
  isCover?: boolean;
  sortOrder?: number;
}

export function useUploadImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UploadImageParams) => {
      const formData = new FormData();
      formData.append("file", params.file);
      formData.append("entityType", params.entityType);
      formData.append("entityId", params.entityId);
      if (params.isCover) formData.append("isCover", "true");
      if (params.sortOrder !== undefined) formData.append("sortOrder", String(params.sortOrder));
      return api.upload<{ image: EventImage }>("/upload/images", formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event-images"] });
      queryClient.invalidateQueries({ queryKey: ["movies"] });
      queryClient.invalidateQueries({ queryKey: ["movie-images"] });
    },
  });
}

export function useDeleteImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) => api.delete(`/upload/images/${imageId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event-images"] });
      queryClient.invalidateQueries({ queryKey: ["movies"] });
      queryClient.invalidateQueries({ queryKey: ["movie-images"] });
    },
  });
}

export function useSetCoverImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) =>
      api.patch<{ image: EventImage }>(`/upload/images/${imageId}/cover`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event-images"] });
      queryClient.invalidateQueries({ queryKey: ["movies"] });
      queryClient.invalidateQueries({ queryKey: ["movie-images"] });
    },
  });
}
